import * as yup from "yup";
import {capitalize} from "@mui/material";
import {useMemo} from "react";
import {useSchedulersHandling} from "./useSchedulersHandling";
import {hasOverlappingTimeSlots} from "../../utils/schedulerTimeSlots";
import type {SchedulerTimeSlotFormValues} from "../../types/schedulerTypes";

type Translation = ReturnType<typeof useSchedulersHandling>["translation"];

/** Shared Yup schema for the scheduler create/update forms. */
export function useSchedulerFormSchema() {
    const {translation} = useSchedulersHandling();

    return useMemo(() => yup.object().shape({
        name: yup.string().required(capitalize(translation('scheduler-attributes-name-required-message'))),
        scope: yup.string().oneOf(['GLOBAL', 'MANDANT', 'AUFGABE']).required(),
        mandantId: yup.string().when('scope', {
            is: (scope: string) => scope !== 'GLOBAL',
            then: (s) => s.required(),
            otherwise: (s) => s.notRequired(),
        }),
        aufgabeId: yup.string().when('scope', {
            is: (scope: string) => scope === 'AUFGABE',
            then: (s) => s.required(),
            otherwise: (s) => s.notRequired(),
        }),
        timeSlots: yup.array().min(1).of(timeSlotSchema(translation)).test(
            'no-overlapping-time-slots',
            capitalize(translation('scheduler-attributes-time-slots-overlap')),
            (value) => !hasOverlappingTimeSlots((value ?? []) as SchedulerTimeSlotFormValues[]),
        ),
        reschedulingStrategies: yup.array().min(1).of(reschedulingStrategySchema(translation)),
    }), [translation]);
}

/** A time slot's end must be after its start. */
function timeSlotSchema(translation: Translation) {
    return yup.object().shape({
        start: yup.string().required(),
        end: yup.string().required().test(
            'end-after-start',
            capitalize(translation('scheduler-attributes-time-slot-end-before-start-message')),
            isEndAfterStart,
        ),
    });
}

function isEndAfterStart(this: yup.TestContext, end?: string): boolean {
    const {start} = this.parent;
    if (!start || !end) return true;
    return end > start;
}

/** Rescheduling strategy attempt numbers must be unique within the scheduler, matching the backend rule. */
function reschedulingStrategySchema(translation: Translation) {
    return yup.object().shape({
        attemptNumber: yup.number().required(),
        strategy: yup.string().required(),
    }).test(
        'unique-attempt-number',
        capitalize(translation('scheduler-attributes-rescheduling-strategy-duplicate-attempt-message')),
        hasUniqueAttemptNumber,
    );
}

function hasUniqueAttemptNumber(this: yup.TestContext, strategy?: { attemptNumber: number }): boolean {
    const siblings = this.parent as { attemptNumber: number }[] | undefined;
    if (!siblings || strategy?.attemptNumber == null) return true;
    return siblings.filter((s) => s.attemptNumber === strategy.attemptNumber).length <= 1;
}
